'use client';
import Dialog from '@/components/ui/Dialog';
import React, { createContext, useContext, useState, ReactNode } from 'react';

type ModalContextType = {
  isOpen: boolean;
  openModal: ({
    content,
    title,
    closeOnClickOutside,
  }: {
    content: ReactNode;
    title?: string;
    closeOnClickOutside?: boolean;
  }) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [isCloseOnClickOutside, setIsCloseOnClickOutside] =
    useState<boolean>(true);

  const openModal = ({
    content,
    title,
    closeOnClickOutside,
  }: {
    content: ReactNode;
    title?: string;
    closeOnClickOutside?: boolean;
  }) => {
    setContent(content);
    setTitle(title);
    setIsCloseOnClickOutside(closeOnClickOutside ?? true);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setContent(null);
    setTitle(undefined);
  };

  return (
    <ModalContext.Provider value={{ isOpen, openModal, closeModal }}>
      <Dialog
        closeOnClickOutside={isCloseOnClickOutside}
        isOpen={isOpen}
        onClose={closeModal}
        title={title}
        maxWidth="md"
      >
        {content}
      </Dialog>
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
